import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ProjectDetailsDto {
  expectedDueDate?: string;
  requestorCAI: string;
  name: string;
  receiveDate: string;
  receivedBy: string;
  assignedToCAI: string;
  measuredDepth?: number | null;
  totalVerticalDepth?: number | null;
  waterDepth?: number | null;
  notes: string;
  rigName: string;
  rigId?: number | null;
  riserless?: boolean;
  isBlendTest: boolean;
  alternateRequestorCAI: string;
  alternateCustomers: string[];
  businessUnitCode: string;
  businessUnitName: string;
  wellName: string;
  wellId: number;
  pipeSize: string;
  openHoleSize: string;
  bhct?: number | null;
  bhst?: number | null;
  mudWeightPerGallon?: number | null;
  testSchedule: string;
  newTimeToTemperature?: number | null;
  mudTypeId: number;
  jobTypeId: number;
  jobSubTypeId: number;
  serviceCompanyId?: number | null;
  serviceCompanyRptNumbers: string;
  customerProvidedWBS: string;
  isPayAsYouGo?: boolean;
  bhp?: number | null;
  wellLocation: string;
  id: number;
  isActive: boolean;
  updateDate?: string;
  updateUser: string;
  projectType: string | number;
  status: string | number;
  sap: string;
  slurryServiceCompanyData: any;
  spacerServiceCompanyData: any;
  qaqcMaterials: any[];
  spacerQAQCTitles: any[];
  redesignReason: any;
}

/** Mirrors CemSysApi.Common.DTOs.ProjectDetails.UpdateProjectRequestDto */
export interface UpdateProjectRequestDto {
  projectType: number;
  ProjStatusID: number;
  name: string;
  receivedBy: string;
  assignedToCAI: string;
  requestorCAI: string;
  alternateRequestorCAI: string;
  alternateCustomers: string[];
  expectedDueDate?: string | null;
  rigId?: number | null;
  customerProvidedWBS: string;
  sap: string;
  isPayAsYouGo?: boolean | null;
  serviceCompanyId?: number | null;
  serviceCompanyRptNumbers: string;
  notes: string;
  mudTypeId: number;
  jobTypeId: number;
  jobSubTypeId: number;
  isBlendTest: boolean;
  riserless?: boolean | null;
  measuredDepth?: number | null;
  totalVerticalDepth?: number | null;
  waterDepth?: number | null;
  pipeSize: string;
  openHoleSize: string;
  bhct?: number | null;
  bhst?: number | null;
  bhp?: number | null;
  mudWeightPerGallon?: number | null;
  newTimeToTemperature?: number | null;
  testSchedule: string;
  slurryServiceCompanyData?: any | null;
  spacerServiceCompanyData?: any | null;
}

export interface SpacerQaqcTitleValueDto {
  id: number;
  title: string;
  groupId: number | null;
  groupName: string | null;
}

export interface SpacerQaqcTitleDto {
  id: number;
  cementProjId: number;
  temperature: number | null;
  fullTitle: string;
  titleValue: SpacerQaqcTitleValueDto;
}

export interface UpdateSpacerQaqcReadingsRequestDto {
  readings: SpacerQaqcTitleDto[];
  removeReadings: SpacerQaqcTitleDto[];
}
export interface AppendNoteRequestDto {
  note: string;
}

export interface AttachmentDto {
  id: number;
  fileName: string;
  extension: string | null;
  description: string;
  uploadedBy: string;
  ownerId: number;
}

export interface TestAttachmentDto {
  id: number;
  fileName: string;
  extension?: string | null;
  description: string;
  uploadedBy: string;
  ownerId: number;
  slurryId?: number;
  slurryName?: string;
  testType?: number;
  testTypeName?: string;
}

export interface UpdateAttachmentDto {
  id: number;
  fileName: string;
  description: string;
  uploadedBy: string;
  ownerId: number;
}
export interface AppendNoteResponseDto {
  success: boolean;
  message: string;
  updatedNotes?: string;
}

@Injectable({ providedIn: 'root' })
export class ProjectDetailsService {

  constructor(private http: HttpClient) {
  }

  private readonly baseUrl = environment.apiUrl;

  private normalizeArrayResponse<T>(response: unknown): T[] {
    if (Array.isArray(response)) {
      return response as T[];
    }

    if (response && typeof response === 'object') {
      const wrappedArray = (response as { $values?: unknown; items?: unknown; data?: unknown; }).$values
        ?? (response as { $values?: unknown; items?: unknown; data?: unknown; }).items
        ?? (response as { $values?: unknown; items?: unknown; data?: unknown; }).data;

      if (Array.isArray(wrappedArray)) {
        return wrappedArray as T[];
      }
    }

    return [];
  }

  getProjectById(id: number): Observable<ProjectDetailsDto> {
    return this.http.get<ProjectDetailsDto>(`${this.baseUrl}/project-details/${id}`);
  }

  getProjectNotes(projectId: number): Observable<string> {
    return this.http.get(`${this.baseUrl}/project-details/${projectId}/notes`, { responseType: 'text' });
  }

  appendProjectNote(projectId: number, note: string): Observable<AppendNoteResponseDto> {
    const request: AppendNoteRequestDto = { note };
    return this.http.post<AppendNoteResponseDto>(`${this.baseUrl}/project-details/${projectId}/notes/append`, request);
  }

  clearProjectNote(projectId: number): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.baseUrl}/project-details/${projectId}/notes/clear`, {});
  }


  /** PUT /api/v1/project-details/{id} — mirrors WPF OnSaveOrUpdate() */
  updateProject(id: number, request: UpdateProjectRequestDto): Observable<ProjectDetailsDto> {
    return this.http.put<ProjectDetailsDto>(`${this.baseUrl}/project-details/${id}`, request);
  }

  /**
   * Mirrors WPF OnCompleteProject → ChangeProjectStatusAsync(projectId, Completed).
   * Backend guards: project must have a non-empty SAP (CanCompleteProject).
   * Returns 204 No Content on success.
   */
  completeProject(projectId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/project-details/${projectId}/complete`, {});
  }
  getAllTitlesForSpacer(): Observable<SpacerQaqcTitleValueDto[]> {
    return this.http.get<SpacerQaqcTitleValueDto[]>(`${this.baseUrl}/project-details/qaqc-titles`);
  }

  updateProjectQaqcReadings(projectId: number, request: UpdateSpacerQaqcReadingsRequestDto): Observable<SpacerQaqcTitleDto[]> {
    return this.http.put<SpacerQaqcTitleDto[]>(`${this.baseUrl}/project-details/qaqc-readings/${projectId}`, request);
  }

  getAttachmentsByProjectId(projectId: number): Observable<AttachmentDto[]> {
    return this.http.get<AttachmentDto[]>(
      `${this.baseUrl}/project-details/attachments/${projectId}`
    );
  }

  getTestAttachmentsByProjectId(projectId: number): Observable<TestAttachmentDto[]> {
    return this.http.get<TestAttachmentDto[] | { $values?: TestAttachmentDto[]; items?: TestAttachmentDto[]; data?: TestAttachmentDto[]; } | null>(
      `${this.baseUrl}/project-details/test-attachments/${projectId}`
    ).pipe(
      map(response => this.normalizeArrayResponse<TestAttachmentDto>(response))
    );
  }

  getTestAttachmentsBySlurryId(slurryId: number): Observable<TestAttachmentDto[]> {
    return this.http.get<TestAttachmentDto[] | { $values?: TestAttachmentDto[]; items?: TestAttachmentDto[]; data?: TestAttachmentDto[]; } | null>(
      `${this.baseUrl}/slurry/${slurryId}/test-attachments`
    ).pipe(
      map(response => this.normalizeArrayResponse<TestAttachmentDto>(response))
    );
  }

  getTestAttachmentsByTestInputMasterId(testInputMasterId: number): Observable<TestAttachmentDto[]> {
    return this.http.get<TestAttachmentDto[] | { $values?: TestAttachmentDto[]; items?: TestAttachmentDto[]; data?: TestAttachmentDto[]; } | null>(
      `${this.baseUrl}/slurry/tests/${testInputMasterId}/attachments`
    ).pipe(
      map(response => this.normalizeArrayResponse<TestAttachmentDto>(response))
    );
  }

  uploadTestAttachment(testInputMasterId: number, formData: FormData): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}/slurry/tests/${testInputMasterId}/attachments`,
      formData
    );
  }

  deleteTestAttachment(testInputMasterId: number, attachmentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/slurry/tests/${testInputMasterId}/attachments/${attachmentId}`
    );
  }

  updateAttachment(dto: UpdateAttachmentDto): Observable<boolean> {
    return this.http.put<boolean>(
      `${this.baseUrl}/project-details/update-attachment`,
      dto
    );
  }

  uploadAttachment(formData: FormData) {
  return this.http.request<boolean>(
    'POST',
      `${this.baseUrl}/project-details/upload-attachment`,
      {
        body: formData,
        responseType: 'json'
      }
    );
  }
deleteAttachment(id: number): Observable<boolean> {
  return this.http.delete<boolean>(
    `${this.baseUrl}/project-details/attachment/${id}`
  );
}
downloadProjectAttachment(projectId: number, attachmentId: number) {
  return this.http.get(
    `${this.baseUrl}/project-details/attachment/${projectId}/${attachmentId}`,
    { responseType: 'blob' }
  );
}

downloadTestAttachment(ownerId: number, attachmentId: number) {
  return this.http.get(
    `${this.baseUrl}/project-details/test-attachment/${ownerId}/${attachmentId}`,
    { responseType: 'blob' }
  );
}

  duplicateSpacerMix(slurryId: number, projectId: number, redesignReason: { reasonTypeId: number; comments: string; complianceTestIds: number[]; } | null): Observable<any> {
    const body: any = { projectId };
    if (redesignReason) {
      body.slurryRedesignReason = redesignReason;
    }
    return this.http.post<any>(`${this.baseUrl}/project-details/${slurryId}/duplicate-spacer`, body);
  }

}
